import { authenticatedFetch } from "@/lib/auth/authenticatedFetch";
import { ApiResponseError, extractApiError } from "@/lib/errors/apiError";
import { resolveApiError } from "@/lib/errors/resolveApiError";
import { getApiBaseUrl } from "@/lib/utils";
import { type UseQueryOptions, queryOptions } from "@tanstack/react-query";

import { type BucketWithType, bucketWithTypeSchema } from "@workspace/schemas/zod";

export const bucketWithTypeByIdQueryOptions = (bucketId: number, options: Omit<UseQueryOptions<BucketWithType>, "queryKey" | "queryFn"> = {}) =>
  queryOptions({
    queryKey: ["buckets", bucketId, "with-types"],
    // No AbortSignal on purpose: this query is router-loaded (ensureQueryData) and read via
    // useSuspenseQuery. Consuming the signal opts it into React Query auto-cancellation, which on an
    // interrupted navigation rejects the loader promise with a CancelledError and crashes the route.
    // Keep it omitted while this stays a suspense route query. See docs/adr/0004.
    queryFn: async () => {
      const baseUrl = getApiBaseUrl();
      const response = await authenticatedFetch(`${baseUrl}/buckets/${bucketId}/with-types`);

      if (!response.ok) {
        const apiError = await extractApiError(response);
        const userMessage = resolveApiError(apiError.error_code, "Bucket konnte nicht geladen werden");
        throw new ApiResponseError(response.status, apiError.error_code, apiError.details, userMessage);
      }

      // Type assertion needed because z.infer doesn't properly type date fields from callback overrides
      return bucketWithTypeSchema.parse(await response.json()) as unknown as BucketWithType;
    },
    ...options,
  });
